import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { qrLoginService } from '../services/qrLoginService';
import { useNotification } from '../hooks/useNotification';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { useUserStore } from '../stores/useUserStore';

export const QRLoginConfirmPage: React.FC = () => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const { user, fetchUser } = useUserStore();
  const [verificationCode, setVerificationCode] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(true);

  // Auto-fill verification code from URL params if present
  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      setVerificationCode(codeFromUrl.toUpperCase());
    }
  }, [searchParams]);

  // Check if user is logged in
  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user, fetchUser]);

  // Redirect if no sessionId
  useEffect(() => {
    if (!sessionId) {
      navigate('/login');
    }
  }, [sessionId, navigate]);

  const handleConfirm = async () => {
    if (!sessionId) {
      setError(translate('pos.auth.qr_login.invalid_session', 'Invalid session ID'));
      return;
    }

    if (!verificationCode || verificationCode.length !== 6) {
      setError(
        translate(
          'pos.auth.qr_login.invalid_code_format',
          'Please enter a valid 6-character verification code'
        )
      );
      return;
    }

    if (!user) {
      setError(
        translate(
          'pos.auth.qr_login.must_be_logged_in',
          'You must be logged in on your mobile device to confirm'
        )
      );
      return;
    }

    try {
      setConfirming(true);
      setError(null);

      const result = await qrLoginService.confirmLogin(sessionId, verificationCode);

      if (result.success) {
        setConfirmed(true);
        notify(
          translate('pos.auth.qr_login.confirmation_success', 'Login confirmed successfully!'),
          { type: 'success' }
        );

        // Close dialog after 2 seconds and redirect
        setTimeout(() => {
          setShowDialog(false);
          navigate('/dashboard');
        }, 2000);
      } else {
        setError(result.message || translate('pos.auth.qr_login.confirmation_failed', 'Confirmation failed'));
      }
    } catch (err) {
      console.error('Failed to confirm login:', err);
      setError(
        err instanceof Error
          ? err.message
          : translate('pos.auth.qr_login.confirmation_error', 'An error occurred during confirmation')
      );
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  if (!sessionId) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {translate('pos.auth.qr_login.confirm_login', 'Confirm Login')}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'pos.auth.qr_login.confirm_description',
                'Please confirm that you want to log in on the other device. The verification code has been auto-filled for you.'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current User Info */}
            {user && (
              <div className="rounded-lg border p-3 bg-muted/50">
                <p className="text-xs text-muted-foreground mb-1">
                  {translate('pos.auth.qr_login.logging_in_as', 'Logging in as')}:
                </p>
                <p className="font-medium">{user.fullName || user.name || user.username}</p>
              </div>
            )}

            {/* Verification Code Input */}
            <div className="space-y-2">
              <Label htmlFor="verification-code">
                {translate('pos.auth.qr_login.verification_code', 'Verification Code')}
              </Label>
              <Input
                id="verification-code"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                  if (value.length <= 6) {
                    setVerificationCode(value);
                    setError(null);
                  }
                }}
                placeholder="ABC123"
                maxLength={6}
                className="font-mono text-lg text-center font-bold tracking-widest uppercase"
                disabled={confirming || confirmed}
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                {translate(
                  'pos.auth.qr_login.code_instructions',
                  'Enter the 6-character verification code shown on the other device'
                )}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {confirmed && (
              <div className="rounded-lg border border-green-500/50 bg-green-500/10 p-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-700 dark:text-green-400">
                    {translate('pos.auth.qr_login.confirmed', 'Confirmed!')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {translate('pos.auth.qr_login.redirecting', 'Redirecting...')}
                  </p>
                </div>
              </div>
            )}

            {/* Not Logged In Warning */}
            {!user && (
              <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-3 flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                    {translate('pos.auth.qr_login.not_logged_in', 'Not logged in')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {translate(
                      'pos.auth.qr_login.must_login_first',
                      'You must be logged in on this device to confirm login on another device.'
                    )}
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={confirming || confirmed}
            >
              {translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!verificationCode || verificationCode.length !== 6 || confirming || confirmed || !user}
            >
              {confirming ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {translate('pos.auth.qr_login.confirming', 'Confirming...')}
                </>
              ) : confirmed ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {translate('pos.auth.qr_login.confirmed', 'Confirmed')}
                </>
              ) : (
                translate('pos.common.action.confirm', 'Confirm')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
